import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ITEquipmentDialog } from "./ITEquipmentDialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const ITEquipmentManagement = () => {
  const { t } = useLanguage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);

  const { data: equipment, isLoading, refetch } = useQuery<any[]>({
    queryKey: ['it-equipment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('it_equipment')
        .select(`
          *,
          suppliers (
            id,
            name
          )
        `)
        .order('equipment_name');
      
      if (error) throw error;
      
      // Get assigned user names separately
      const equipmentWithUsers = await Promise.all(
        (data || []).map(async (item) => {
          if (item.assigned_to) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', item.assigned_to)
              .single();
            return { ...item, assigned_name: profile?.full_name };
          }
          return item;
        })
      );
      
      return equipmentWithUsers;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('it_equipment')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success(t('deletedSuccessfully'));
      refetch();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast.error(t('errorDeletingData'));
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      active: "bg-green-500",
      maintenance: "bg-yellow-500",
      retired: "bg-gray-500",
    };
    return <Badge className={colors[status]}>{t(status)}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('itEquipment')}</h3>
          <p className="text-sm text-muted-foreground">{t('itEquipmentManagement')}</p>
        </div>
        <Button onClick={() => { setSelectedEquipment(null); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('addEquipment')}
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('equipmentName')}</TableHead>
              <TableHead>{t('equipmentType')}</TableHead>
              <TableHead>{t('model')}</TableHead>
              <TableHead>{t('assignedTo')}</TableHead>
              <TableHead>{t('status')}</TableHead>
              <TableHead className="text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('loading')}</TableCell>
              </TableRow>
            ) : equipment?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('noData')}</TableCell>
              </TableRow>
            ) : (
              equipment?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.equipment_name}</TableCell>
                  <TableCell>{t(item.equipment_type)}</TableCell>
                  <TableCell>{item.model || '-'}</TableCell>
                  <TableCell>{item.assigned_name || '-'}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedEquipment(item); setIsDialogOpen(true); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <ITEquipmentDialog
        open={isDialogOpen}
        onClose={() => { setIsDialogOpen(false); setSelectedEquipment(null); refetch(); }}
        equipment={selectedEquipment}
      />
    </div>
  );
};